import React, { useEffect, useState, useCallback } from 'react';
import { useSessionStore } from '../store/sessionStore';
import { Session } from '../types';
import { formatDistanceToNow, format, startOfDay, subDays, eachDayOfInterval } from 'date-fns';
import {
  ChartBarIcon,
  UserGroupIcon,
  CheckIcon,
  TrophyIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';

interface SessionStats {
  session: Session;
  totalChatters: number;
  greetedChatters: number;
  pendingChatters: number;
  greetedPercentage: number;
  avgGreetingTime: number;
  dailyActivity: { date: string; chatters: number; greeted: number }[];
}

interface AggregateStats {
  totalSessions: number;
  activeSessions: number;
  totalChatters: number;
  totalGreeted: number;
  overallGreetedPercentage: number;
  avgChattersPerSession: number;
  longestSession: Session | null;
  mostActivePeriod: { date: string; chatters: number } | null;
  recentActivity: { date: string; chatters: number; greeted: number }[];
}

const StatisticsPage: React.FC = () => {
  const { sessions, isLoading, fetchSessions } = useSessionStore();
  const [selectedSessionId, setSelectedSessionId] = useState<string>('all');
  const [sessionStats, setSessionStats] = useState<SessionStats[]>([]);
  const [aggregateStats, setAggregateStats] = useState<AggregateStats | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['overview']));

  const calculateStatistics = useCallback(() => {
    // Calculate per-session statistics
    const sessionStatsData: SessionStats[] = sessions.map(session => {
      const chatters = session.chatters || [];
      const totalChatters = chatters.length;
      const greetedChatters = chatters.filter(c => c.isGreeted).length;
      const pendingChatters = totalChatters - greetedChatters;
      const greetedPercentage = totalChatters > 0 ? Math.round((greetedChatters / totalChatters) * 100) : 0;
      
      // Calculate average greeting time (time between first seen and greeted)
      const greetedWithTime = chatters.filter(c => c.isGreeted && c.greetedAt);
      const avgGreetingTime = greetedWithTime.length > 0 
        ? greetedWithTime.reduce((sum, c) => {
            const firstSeen = new Date(c.firstSeenAt).getTime();
            const greeted = new Date(c.greetedAt!).getTime();
            return sum + (greeted - firstSeen);
          }, 0) / greetedWithTime.length
        : 0;

      // Daily activity for the last 7 days
      const last7Days = eachDayOfInterval({
        start: subDays(new Date(), 6),
        end: new Date()
      });

      const dailyActivity: { date: string; chatters: number; greeted: number }[] = last7Days.map(date => {
        const dayStart = startOfDay(date);
        const dayEnd = new Date(dayStart);
        dayEnd.setDate(dayEnd.getDate() + 1);

        const dayChatters = chatters.filter(c => {
          const firstSeen = new Date(c.firstSeenAt);
          return firstSeen >= dayStart && firstSeen < dayEnd;
        });

        const dayGreeted = dayChatters.filter(c => c.isGreeted).length;

        return {
          date: format(date, 'MMM dd'),
          chatters: dayChatters.length,
          greeted: dayGreeted
        };
      });

      return {
        session,
        totalChatters,
        greetedChatters,
        pendingChatters,
        greetedPercentage,
        avgGreetingTime,
        dailyActivity
      };
    });

    setSessionStats(sessionStatsData);

    // Calculate aggregate statistics
    const allChatters = sessions.flatMap(s => s.chatters || []);
    const totalSessions = sessions.length;
    const activeSessions = sessions.filter(s => s.isActive).length;
    const totalChatters = allChatters.length;
    const totalGreeted = allChatters.filter(c => c.isGreeted).length;
    const overallGreetedPercentage = totalChatters > 0 ? Math.round((totalGreeted / totalChatters) * 100) : 0;
    const avgChattersPerSession = totalSessions > 0 ? Math.round(totalChatters / totalSessions) : 0;

    // Find longest session by duration
    const longestSession = sessions.reduce((longest, current) => {
      const currentDuration = current.endedAt 
        ? new Date(current.endedAt).getTime() - new Date(current.startedAt).getTime()
        : new Date().getTime() - new Date(current.startedAt).getTime();
      
      if (!longest) return current;
      
      const longestDuration = longest.endedAt
        ? new Date(longest.endedAt).getTime() - new Date(longest.startedAt).getTime()
        : new Date().getTime() - new Date(longest.startedAt).getTime();
      
      return currentDuration > longestDuration ? current : longest;
    }, null as Session | null);

    // Recent activity across all sessions
    const last7Days = eachDayOfInterval({
      start: subDays(new Date(), 6),
      end: new Date()
    });

    const recentActivity = last7Days.map(date => {
      const dayStart = startOfDay(date);
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);

      const dayChatters = allChatters.filter(c => {
        const firstSeen = new Date(c.firstSeenAt);
        return firstSeen >= dayStart && firstSeen < dayEnd;
      });

      const dayGreeted = dayChatters.filter(c => c.isGreeted).length;

      return {
        date: format(date, 'MMM dd'),
        chatters: dayChatters.length,
        greeted: dayGreeted
      };
    });

    // Find most active day
    const mostActivePeriod = recentActivity.reduce((most, current) => {
      return !most || current.chatters > most.chatters ? current : most;
    }, null as { date: string; chatters: number } | null);

    setAggregateStats({
      totalSessions,
      activeSessions,
      totalChatters,
      totalGreeted,
      overallGreetedPercentage,
      avgChattersPerSession,
      longestSession,
      mostActivePeriod,
      recentActivity
    });
  }, [sessions]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    if (sessions.length > 0) {
      calculateStatistics();
    }
  }, [sessions, calculateStatistics]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const formatDuration = (milliseconds: number) => {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const selectedStats = selectedSessionId === 'all' 
    ? null 
    : sessionStats.find(s => s.session.id === selectedSessionId);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <>
      <div className="px-6 lg:px-12 py-8 flex-1">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white light:text-gray-900 mb-2">Statistics</h1>
          <p className="text-gray-400 light:text-gray-600">Analyze your greeting performance and chat engagement</p>
        </div>

        {/* Session Selector */}
        <div className="card p-6 mb-8">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-300 light:text-gray-700">
              View statistics for:
            </label>
            <select
              value={selectedSessionId}
              onChange={(e) => setSelectedSessionId(e.target.value)}
              className="bg-gray-700 light:bg-white border border-gray-600 light:border-gray-300 rounded-lg px-3 py-2 text-white light:text-gray-900 focus:outline-none focus:ring-2 focus:ring-twitch-500"
            >
              <option value="all">All Sessions (Aggregate)</option>
              {sessions.map(session => (
                <option key={session.id} value={session.id}>
                  {session.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedSessionId === 'all' ? (
          /* Aggregate Statistics */
          aggregateStats && (
            <div className="space-y-8">
              {/* Overview Stats */}
              <div>
                <button
                  onClick={() => toggleSection('overview')}
                  className="flex items-center justify-between w-full text-left mb-4"
                >
                  <h2 className="text-xl font-semibold text-white light:text-gray-900">Overview</h2>
                  {expandedSections.has('overview') ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                
                {expandedSections.has('overview') && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="card p-6">
                      <div className="flex items-center">
                        <ChartBarIcon className="h-8 w-8 text-twitch-600 mr-4" />
                        <div>
                          <dt className="text-sm font-medium text-gray-400 light:text-gray-600">Total Sessions</dt>
                          <dd className="text-2xl font-bold text-white light:text-gray-900">{aggregateStats.totalSessions}</dd>
                        </div>
                      </div>
                    </div>

                    <div className="card p-6">
                      <div className="flex items-center">
                        <UserGroupIcon className="h-8 w-8 text-blue-600 mr-4" />
                        <div>
                          <dt className="text-sm font-medium text-gray-400 light:text-gray-600">Total Chatters</dt>
                          <dd className="text-2xl font-bold text-white light:text-gray-900">{aggregateStats.totalChatters}</dd>
                        </div>
                      </div>
                    </div>

                    <div className="card p-6">
                      <div className="flex items-center">
                        <CheckIcon className="h-8 w-8 text-green-600 mr-4" />
                        <div>
                          <dt className="text-sm font-medium text-gray-400 light:text-gray-600">Total Greeted</dt>
                          <dd className="text-2xl font-bold text-white light:text-gray-900">{aggregateStats.totalGreeted}</dd>
                        </div>
                      </div>
                    </div>

                    <div className="card p-6">
                      <div className="flex items-center">
                        <TrophyIcon className="h-8 w-8 text-yellow-600 mr-4" />
                        <div>
                          <dt className="text-sm font-medium text-gray-400 light:text-gray-600">Greeting Rate</dt>
                          <dd className="text-2xl font-bold text-white light:text-gray-900">{aggregateStats.overallGreetedPercentage}%</dd>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Performance Metrics */}
              <div>
                <button
                  onClick={() => toggleSection('performance')}
                  className="flex items-center justify-between w-full text-left mb-4"
                >
                  <h2 className="text-xl font-semibold text-white light:text-gray-900">Performance Metrics</h2>
                  {expandedSections.has('performance') ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {expandedSections.has('performance') && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="card p-6">
                      <h3 className="text-lg font-medium text-white light:text-gray-900 mb-2">Average Chatters per Session</h3>
                      <p className="text-3xl font-bold text-twitch-400">{aggregateStats.avgChattersPerSession}</p>
                    </div>

                    <div className="card p-6">
                      <h3 className="text-lg font-medium text-white light:text-gray-900 mb-2">Active Sessions</h3>
                      <p className="text-3xl font-bold text-green-400">{aggregateStats.activeSessions}</p>
                      <p className="text-sm text-gray-400 light:text-gray-600 mt-1">
                        of {aggregateStats.totalSessions} total
                      </p>
                    </div>

                    <div className="card p-6">
                      <h3 className="text-lg font-medium text-white light:text-gray-900 mb-2">Longest Session</h3>
                      {aggregateStats.longestSession ? (
                        <>
                          <p className="text-lg font-semibold text-white light:text-gray-900 truncate">
                            {aggregateStats.longestSession.name}
                          </p>
                          <p className="text-sm text-gray-400 light:text-gray-600">
                            {formatDistanceToNow(new Date(aggregateStats.longestSession.startedAt), { addSuffix: true })}
                          </p>
                        </>
                      ) : (
                        <p className="text-gray-400 light:text-gray-600">No sessions yet</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Recent Activity */}
              <div>
                <button
                  onClick={() => toggleSection('activity')}
                  className="flex items-center justify-between w-full text-left mb-4"
                >
                  <h2 className="text-xl font-semibold text-white light:text-gray-900">Recent Activity (Last 7 Days)</h2>
                  {expandedSections.has('activity') ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {expandedSections.has('activity') && (
                  <div className="card p-6">
                    {aggregateStats.mostActivePeriod && (
                      <div className="mb-6">
                        <p className="text-sm text-gray-400 light:text-gray-600 mb-2">Most Active Day</p>
                        <p className="text-lg font-semibold text-white light:text-gray-900">
                          {aggregateStats.mostActivePeriod.date} - {aggregateStats.mostActivePeriod.chatters} chatters
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-7 gap-2">
                      {aggregateStats.recentActivity.map((day, index) => (
                        <div key={index} className="text-center">
                          <p className="text-xs text-gray-400 light:text-gray-600 mb-2">{day.date}</p>
                          <div className="bg-gray-700 light:bg-gray-200 rounded-lg p-3">
                            <p className="text-sm font-semibold text-white light:text-gray-900">{day.chatters}</p>
                            <p className="text-xs text-gray-400 light:text-gray-600">chatters</p>
                            <p className="text-xs text-green-400 light:text-green-600 mt-1">{day.greeted} greeted</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        ) : (
          /* Individual Session Statistics */
          selectedStats && (
            <div className="space-y-8">
              {/* Session Overview */}
              <div className="card p-6">
                <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">
                  {selectedStats.session.name}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div>
                    <dt className="text-sm font-medium text-gray-400 light:text-gray-600">Total Chatters</dt>
                    <dd className="text-2xl font-bold text-white light:text-gray-900">{selectedStats.totalChatters}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-400 light:text-gray-600">Greeted</dt>
                    <dd className="text-2xl font-bold text-green-400">{selectedStats.greetedChatters}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-400 light:text-gray-600">Pending</dt>
                    <dd className="text-2xl font-bold text-yellow-400">{selectedStats.pendingChatters}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-400 light:text-gray-600">Completion Rate</dt>
                    <dd className="text-2xl font-bold text-twitch-400">{selectedStats.greetedPercentage}%</dd>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-700 light:border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <dt className="text-sm font-medium text-gray-400 light:text-gray-600">Session Status</dt>
                      <dd className={`text-lg font-semibold ${
                        selectedStats.session.isActive ? 'text-green-400' : 'text-gray-400'
                      }`}>
                        {selectedStats.session.isActive ? 'Active' : 'Inactive'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-400 light:text-gray-600">Average Greeting Time</dt>
                      <dd className="text-lg font-semibold text-white light:text-gray-900">
                        {selectedStats.avgGreetingTime > 0 ? formatDuration(selectedStats.avgGreetingTime) : 'N/A'}
                      </dd>
                    </div>
                  </div>
                </div>
              </div>

              {/* Daily Activity Chart */}
              <div className="card p-6">
                <h3 className="text-lg font-semibold text-white light:text-gray-900 mb-4">
                  Daily Activity (Last 7 Days)
                </h3>
                <div className="grid grid-cols-7 gap-2">
                  {selectedStats.dailyActivity.map((day, index) => (
                    <div key={index} className="text-center">
                      <p className="text-xs text-gray-400 light:text-gray-600 mb-2">{day.date}</p>
                      <div className="bg-gray-700 light:bg-gray-200 rounded-lg p-3">
                        <p className="text-sm font-semibold text-white light:text-gray-900">{day.chatters}</p>
                        <p className="text-xs text-gray-400 light:text-gray-600">chatters</p>
                        <p className="text-xs text-green-400 light:text-green-600 mt-1">{day.greeted} greeted</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        )}
      </div>
      
      <Footer />
    </>
  );
};

export default StatisticsPage; 